import { LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { Button, Card, Divider, Flex, Form, Space, Tag, Upload } from 'antd'
import { FormInstance } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import { useStocksServiceGetInternalIssuanceEndpoint } from 'src/api/queries'
import {
  InternalInventoryDocumentState,
  InternalInventoryDocumentWithInquiryItemsResponse
} from 'src/api/requests'
import { CustomItemRender, LoadingScreen, Text } from 'src/components'
import { getStatus } from 'src/features/stock/utils'
import { useStyles } from 'src/features/stock-administration/components/Applications/styles'
import {
  LocalAdminConsumableTable,
  LocalCommentSectionAdmin
} from 'src/features/stock-local/components/applications/issuance'
import { FileDocument, onDownloadFile } from 'src/hooks'
import { dayMonthYearHourMinutesFormatSeparator } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { toDayjs } from 'src/utils'

type Props = {
  data?: InternalInventoryDocumentWithInquiryItemsResponse
  form: FormInstance
}

export const StockLocalAdminIssuanceApplicationsAcceptedDetails = ({
  data,
  form
}: Props) => {
  const { t } = useLocales()
  const navigate = useNavigate()

  const { id } = useParams()
  const palette = useTheme()
  const { styles } = useStyles()

  const printableRef = useRef<HTMLDivElement>(null)
  const isRejected = data?.state === InternalInventoryDocumentState.REJECTED

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Application_${id}`
  })

  const onBackClick = () => {
    navigate(paths.stockLocal.applications)
  }

  const { data: issuanceData, isLoading } =
    useStocksServiceGetInternalIssuanceEndpoint(
      {
        id: String(data?.relatedDocuments?.[0]?.id)
      },
      [String(data?.relatedDocuments?.[0]?.id)]
    )

  useEffect(() => {
    if (issuanceData) {
      const initialFormValues = {
        inquiryItems: data?.items?.map((item) => ({
          name: `${item?.internationalName?.name} ${item?.internationalName?.dosage}`,
          internationalNameId: item?.internationalName?.id,
          form: {
            name: item?.productForm?.name,
            id: item?.productForm?.id
          },
          quantity: item?.quantity,
          items: item?.roleStocks || []
        })),
        medicine: issuanceData?.items?.map((issuanceItem) => ({
          barCode: issuanceItem?.barCode,
          expirationDate: toDayjs(issuanceItem?.expirationDate),
          inquiryItemId: issuanceItem?.id,
          name: `${issuanceItem?.internationalName?.name} ${issuanceItem.internationalName?.dosage}`,
          quantity: issuanceItem?.quantity,
          currentQuantity: issuanceItem?.quantity,
          unit: {
            name: issuanceItem.productForm?.name,
            id: issuanceItem?.productForm?.id
          }
        }))
      }

      form.setFieldsValue(initialFormValues)
    }
  }, [issuanceData])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <Flex vertical gap={24}>
      <Flex justify="space-between">
        <Button
          icon={<LeftOutlined />}
          className="fit-content"
          onClick={onBackClick}
          disabled={false}
        >
          {t('common.back')}
        </Button>

        <Space size={8}>
          <Button
            icon={<PrinterOutlined />}
            disabled={false}
            onClick={() => onPrint()}
          >
            {t('stock_applications.print')}
          </Button>
        </Space>
      </Flex>

      <Flex vertical gap={16} ref={printableRef}>
        <Card>
          <Flex vertical gap={16}>
            <Flex vertical gap={8}>
              <Flex justify="space-between">
                <Space>
                  <Text weight={400}>
                    {t('stock_applications.issuance_based_on_inquire')}:
                  </Text>
                  <Text strong>#{data?.code}</Text>
                </Space>
                <Tag
                  color={
                    getStatus(
                      t,
                      data?.state ?? InternalInventoryDocumentState.NEW
                    ).color
                  }
                >
                  {
                    getStatus(
                      t,
                      data?.state ?? InternalInventoryDocumentState.NEW
                    ).text
                  }
                </Tag>
              </Flex>

              <Space>
                <Text weight={400}>
                  {t('stock_applications.request_date')}:
                </Text>
                <Text strong>
                  {dayjs(data?.documentDate).format(
                    dayMonthYearHourMinutesFormatSeparator
                  )}
                </Text>
              </Space>

              {data?.processedOn && (
                <Space>
                  <Text weight={400}>
                    {isRejected
                      ? `${t('stock_applications.refusal_date')}:`
                      : `${t('stock_applications.accepted_date')}:`}
                  </Text>
                  <Text strong>
                    {dayjs(data?.processedOn).format(
                      dayMonthYearHourMinutesFormatSeparator
                    )}
                  </Text>
                </Space>
              )}

              <Space>
                <Text weight={400}>
                  {t('stock_applications.initiated_by')}:
                </Text>
                <Text strong>
                  {data?.initializedBy?.firstName}{' '}
                  {data?.initializedBy?.lastName} - {data?.role?.name}
                </Text>
              </Space>

              {data?.processedBy && (
                <Space>
                  <Text weight={400}>
                    {isRejected
                      ? `${t('stock_applications.refused_by')}:`
                      : `${t('stock_applications.accepted_by')}:`}
                  </Text>
                  <Text strong>
                    {data?.processedBy?.firstName} {data?.processedBy?.lastName}{' '}
                    -
                  </Text>
                  <Text strong>{data?.role?.name}</Text>
                </Space>
              )}
            </Flex>
          </Flex>
        </Card>

        <Card>
          <Flex vertical gap={16}>
            <LocalAdminConsumableTable
              isEdit={data?.state !== InternalInventoryDocumentState.REJECTED}
              data={data}
              issuanceData={issuanceData}
              form={form}
              isSubmitted={true}
            />

            {data?.beneficiaryComment && isRejected && (
              <Card
                title={t('epidemiology.refuse_reason')}
                className={styles.commentCardWrapper}
              >
                <Divider className={styles.commentDivider} />
                <Flex vertical gap={8} className={styles.commentCard}>
                  <div className={styles.commentBeneficiary}>
                    {data?.beneficiaryComment}
                  </div>
                  <Text size="lg" color={palette.colorTextTertiary}>
                    {data?.beneficiaryCommentBy?.firstName}{' '}
                    {data?.beneficiaryCommentBy?.lastName} - {data?.role?.name}
                  </Text>
                </Flex>
              </Card>
            )}

            <Card title={t('stock_applications.uploaded_documents')}>
              <Form.Item name="docs" className="full-width">
                <Upload
                  itemRender={(_, file: FileDocument) => (
                    <CustomItemRender
                      file={file}
                      onDownload={() => onDownloadFile(t, file?.id, file?.name)}
                    />
                  )}
                  fileList={data?.mediaFiles?.map((file, index) => ({
                    ...file,
                    uid: String(index),
                    name: file?.name || `file-${index}`
                  }))}
                  disabled
                />
              </Form.Item>
            </Card>

            <LocalCommentSectionAdmin data={data} issuanceData={issuanceData} />
          </Flex>
        </Card>
      </Flex>
    </Flex>
  )
}
